import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, TrendingUp, Play } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function ExplorePage() {
  const [streams, setStreams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("todas");

  useEffect(() => {
    Promise.all([
      api.get("/discover/streams").then((r) => setStreams(r.data)),
      api.get("/discover/categories").then((r) => setCategories(r.data)),
    ]).catch(() => {});
  }, []);

  const filtered = streams.filter((s) => {
    if (q && !(s.title.toLowerCase().includes(q.toLowerCase()) || s.creator.toLowerCase().includes(q.toLowerCase()) || s.game.toLowerCase().includes(q.toLowerCase()))) return false;
    if (tab === "todas") return true;
    return s.game?.toLowerCase().includes(tab.toLowerCase());
  });

  const tabs = ["todas", ...Array.from(new Set(streams.map(s => s.game))).slice(0,4)];

  return (
    <main className="flex-1 min-w-0 overflow-y-auto scrollbar-thin" data-testid="explore-page">
      {/* Hero */}
      <section className="relative border-b border-[hsl(var(--nx-border))] overflow-hidden">
        <div className="absolute inset-0 nx-grid-bg opacity-40 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[hsl(var(--nx-magenta)/0.14)] blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[hsl(var(--nx-cyan)/0.14)] blur-[120px] pointer-events-none" />

        <div className="relative px-6 lg:px-10 py-10 lg:py-14 max-w-6xl">
          <div className="nx-chip nx-chip-blue mb-3"><TrendingUp className="w-3.5 h-3.5" /> Explorar</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
            O que está <span className="nx-text-gradient">acontecendo</span> agora.
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl">Descubra criadores em ascensão, categorias em alta e comunidades ativas.</p>

          <div className="mt-6 relative max-w-lg">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Busque por criador, jogo ou categoria"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10 h-11 rounded-xl bg-[hsl(var(--input))] border-[hsl(var(--nx-border))]"
              data-testid="explore-search-input"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="px-6 lg:px-10 pt-10 max-w-6xl">
        <div className="flex items-end justify-between mb-4">
          <h2 className="font-display font-bold text-2xl">Categorias populares</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((c) => (
            <div key={c.id} data-testid={`category-${c.id}`} className="group nx-panel overflow-hidden hover:border-[hsl(var(--nx-cyan))] transition-colors cursor-pointer">
              <div className="aspect-[3/4] overflow-hidden relative">
                <img src={c.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="text-white text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-white/70 text-[10px]">{c.viewers.toLocaleString("pt-BR")} assistindo</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Streams */}
      <section className="px-6 lg:px-10 pt-12 pb-14 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-4 gap-3">
          <h2 className="font-display font-bold text-2xl">Lives em destaque</h2>
          <div className="flex flex-wrap gap-1.5 nx-panel-2 p-1 rounded-full">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`explore-tab-${t}`}
                className={`px-3.5 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold transition-colors
                ${tab === t ? "nx-btn-blue text-white" : "text-muted-foreground hover:text-foreground"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-muted-foreground">Nenhuma live encontrada para "{q}".</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((s) => (
              <Link
                to={`/app/live/${s.id}`}
                key={s.id}
                data-testid={`stream-card-${s.id}`}
                className="group block nx-panel overflow-hidden hover:border-[hsl(var(--nx-blue))] transition-colors nx-fade-up"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img src={s.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute top-2.5 left-2.5 nx-chip nx-chip-red !py-1 !px-2">
                    <span className="nx-live-dot" style={{background:'#fff'}} /> AO VIVO
                  </div>
                  <div className="absolute top-2.5 right-2.5 nx-chip !py-1 !px-2">{s.viewers.toLocaleString("pt-BR")}</div>
                  <div className="absolute bottom-2.5 left-2.5 right-2.5">
                    <div className="text-white font-semibold text-sm truncate">{s.title}</div>
                    <div className="text-white/70 text-[11px] truncate">@{s.creator} · {s.game}</div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="flex flex-wrap gap-1.5">
                    {s.tags.slice(0,3).map((t) => (
                      <span key={t} className="nx-chip !text-[9px] !py-0.5 !px-2">{t}</span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
